import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { ClaimProgressStore } from '../../stores';
import { Spinner } from '../base';
import { ClaimActivityFeed } from './claim-activity-feed';
import { ClaimFinancialSummary } from './claim-financial-summary';
import { ClaimFinancialSummaryData } from './claim-financial-summary.utils';
import { ClaimLiveStatusCard } from './claim-live-status-card';
import { deriveClaimProgress, deriveCurrentClaimProgress, IClaimProgressContext } from './claim-progress-derivation';
import { ClaimStageTracker } from './claim-stage-tracker';
import * as S from './claim-progress-tracker.styles';

interface IClaimProgressTrackerProps extends IClaimProgressContext {
    claimId: string;
    claimProgressStore: ClaimProgressStore;
    /**
     * Legacy: callers that still want the financial tiles inside this card (enter-provider) pass them here.
     * The support claim page renders them under Claim Overview instead and leaves this unset.
     */
    financialsLoading?: boolean;
    financials?: ClaimFinancialSummaryData;
}

const TITLE = 'Claim Progress';

interface IClaimProgressTrackerCardProps {
    expanded: boolean;
    onToggleExpanded: () => void;
    financialsLoading?: boolean;
    financials?: ClaimFinancialSummaryData;
    children?: React.ReactNode;
}

const ClaimProgressTrackerCard: React.FC<IClaimProgressTrackerCardProps> = ({
    expanded,
    onToggleExpanded,
    financialsLoading,
    financials,
    children,
}) => (
    <S.StyledTitleCard
        size="large"
        title={TITLE}
        caption={
            <S.HeaderToggleButton
                type="button"
                variant="text"
                onClick={onToggleExpanded}
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide claim progress details' : 'Show claim progress details'}
            >
                <S.HeaderToggleIcon path={expanded ? mdiChevronUp : mdiChevronDown} size={1} />
            </S.HeaderToggleButton>
        }
    >
        {/* Collapsed with nothing to show (the support page passes no financials): header only, no empty body. */}
        {((!financialsLoading && financials) || expanded) && (
            <S.CardBody $expanded={expanded}>
                {!financialsLoading && financials && <ClaimFinancialSummary financials={financials} />}
                {expanded && children}
            </S.CardBody>
        )}
    </S.StyledTitleCard>
);

const ClaimProgressTrackerContent: React.FC<
    Pick<IClaimProgressTrackerProps, 'claimProgressStore' | 'hasAdditionalCoverage' | 'closeout'>
> = observer(({ claimProgressStore, hasAdditionalCoverage, closeout }) => {
    // The store replaces `data` wholesale on every fetch, so the reference is a sound memo key.
    const history = claimProgressStore.data;
    const events = useMemo(() => deriveClaimProgress(history), [history]);
    // Keyed on the closeout's values, not its identity: the caller builds a fresh object each render.
    const closed = closeout?.closed;
    const voided = closeout?.voided;
    const description = closeout?.description;
    const current = useMemo(
        () =>
            deriveCurrentClaimProgress(events, {
                hasAdditionalCoverage,
                closeout: closed === undefined ? undefined : { closed, voided, description },
            }),
        [events, hasAdditionalCoverage, closed, voided, description],
    );

    return (
        <>
            <ClaimStageTracker stages={current.stages} />
            <ClaimLiveStatusCard current={current} />
            <ClaimActivityFeed events={events} />
        </>
    );
});

const ClaimProgressTrackerComponent: React.FC<IClaimProgressTrackerProps> = ({
    claimId,
    claimProgressStore,
    financialsLoading,
    financials,
    hasAdditionalCoverage,
    closeout,
}) => {
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        claimProgressStore.fetch(claimId);

        return () => {
            claimProgressStore.reset();
        };
    }, [claimId, claimProgressStore]);

    const cardProps = {
        expanded,
        onToggleExpanded: () => setExpanded(prev => !prev),
        financialsLoading,
        financials,
    };

    if (claimProgressStore.loading) {
        return (
            <ClaimProgressTrackerCard {...cardProps}>
                <S.LoadingContainer>
                    <Spinner />
                </S.LoadingContainer>
            </ClaimProgressTrackerCard>
        );
    }

    // A failed request is "unknown", never an empty or negative progress (NS-3).
    if (claimProgressStore.error) {
        return (
            <ClaimProgressTrackerCard {...cardProps}>
                <S.ErrorContainer>
                    <S.ErrorMessage>{claimProgressStore.error}</S.ErrorMessage>
                    <S.RetryButton variant="primary" onClick={() => claimProgressStore.fetch(claimId)}>
                        Retry
                    </S.RetryButton>
                </S.ErrorContainer>
            </ClaimProgressTrackerCard>
        );
    }

    return (
        <ClaimProgressTrackerCard {...cardProps}>
            <ClaimProgressTrackerContent
                claimProgressStore={claimProgressStore}
                hasAdditionalCoverage={hasAdditionalCoverage}
                closeout={closeout}
            />
        </ClaimProgressTrackerCard>
    );
};

export const ClaimProgressTracker = observer(ClaimProgressTrackerComponent);
